import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { HotAirBalloonIcon } from '@hugeicons-pro/core-stroke-rounded';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { Heading } from '@/components/gluestack/heading';
import { Button, ButtonText } from '@/components/gluestack/button';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { SheetScrollView } from '@/components/ui/SheetScrollView';
import {
  DRAWER_SCROLL_CLASSES,
  type IContextSheetContentProps,
  type ISheetConfig,
} from '@/components/ui/ContextSheet';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { APP_VERSION } from '@/constants/version';
import {
  whatsNew,
  matchesContext,
  WHATS_NEW_AUTO_SHOW_DISABLED_STORAGE_KEY,
  compareVersions,
  findLatestVersionWithSubstantiveContent,
  type IWhatsNewItem,
  type IWhatsNewVersion,
  type WhatsNewMatchPlatform,
} from '@/constants/whatsNew';

interface IWhatsNewSection {
  key: string;
  /** Fallback single-line heading (unused for current and lineage sections). */
  heading: string;
  /** Displayed content version span through installed app version. */
  currentVersionMeta?: {
    minVersion: string;
    maxVersion: string;
    mode: 'current' | 'catchUp' | 'recent';
  };
  lineageMeta?: {
    lineage: string;
    minVersion: string;
    maxVersion: string;
  };
  items: IWhatsNewItem[];
}

interface IWhatsNewSheetPayload {
  source?: 'auto' | 'manual';
  lastSeenVersion?: string | null;
}

/** Major.minor segment (e.g. "6.0.180" → "6.0"); used to group patch releases under one lineage. */
function versionLineage(versionStr: string): string {
  const parts = versionStr.split('.');
  if (parts.length >= 2) {
    return `${parts[0]}.${parts[1]}`;
  }
  return parts[0] ?? versionStr;
}

function buildSections(
  versions: IWhatsNewVersion[],
  context: { platform: WhatsNewMatchPlatform; isLargeScreen: boolean },
  displayVersion: string,
  lastSeenVersion?: string | null,
): IWhatsNewSection[] {
  const sections: IWhatsNewSection[] = [];
  let currentSection: IWhatsNewSection | null = null;
  let currentLineageSection: IWhatsNewSection | null = null;
  const groupAllByLineage = lastSeenVersion === null;
  const isCatchUp = lastSeenVersion != null && compareVersions(displayVersion, lastSeenVersion) > 0;
  const isRecent = lastSeenVersion === undefined && compareVersions(displayVersion, APP_VERSION) < 0;
  const catchUpLastSeenVersion = isCatchUp ? lastSeenVersion : null;
  const appVersionLineage = versionLineage(APP_VERSION);

  const prependVersionItems = (section: IWhatsNewSection, items: IWhatsNewItem[]) => {
    const seenDedupeKeys = new Set(section.items.map((item) => item.dedupeKey).filter(Boolean));
    const itemsToPrepend: IWhatsNewItem[] = [];
    items.forEach((item) => {
      if (item.dedupeKey) {
        if (seenDedupeKeys.has(item.dedupeKey)) return;
        seenDedupeKeys.add(item.dedupeKey);
      }
      itemsToPrepend.push(item);
    });
    section.items = [...itemsToPrepend, ...section.items];
  };

  versions.forEach((version) => {
    if (compareVersions(version.version, APP_VERSION) > 0) return;

    const matchingItems = version.items.filter((item) => matchesContext(item, context));
    if (matchingItems.length === 0) return;

    const isAfterLastSeen =
      catchUpLastSeenVersion != null &&
      compareVersions(version.version, catchUpLastSeenVersion) > 0;
    const belongsToTopSection = !groupAllByLineage && (isCatchUp
      ? isAfterLastSeen
      : version.version === displayVersion);

    if (belongsToTopSection) {
      if (!currentSection) {
        currentSection = {
          key: `version-${displayVersion}`,
          heading: '',
          currentVersionMeta: {
            minVersion: version.version,
            maxVersion: APP_VERSION,
            mode: isCatchUp ? 'catchUp' : isRecent ? 'recent' : 'current',
          },
          items: [],
        };
        sections.push(currentSection);
      } else if (
        currentSection.currentVersionMeta &&
        compareVersions(version.version, currentSection.currentVersionMeta.minVersion) < 0
      ) {
        currentSection.currentVersionMeta.minVersion = version.version;
      }
      prependVersionItems(currentSection, matchingItems);
      currentLineageSection = null;
      return;
    }

    const belongsToLineageSection = groupAllByLineage || (isCatchUp
      ? !isAfterLastSeen
      : compareVersions(version.version, displayVersion) < 0);

    if (!belongsToLineageSection) return;

    const lineage = versionLineage(version.version);
    const lineageMaxVersion =
      groupAllByLineage && lineage === appVersionLineage ? APP_VERSION : version.version;
    const lineageSectionKey = `lineage-${lineage}`;
    if (currentLineageSection && currentLineageSection.key === lineageSectionKey) {
      const meta = currentLineageSection.lineageMeta;
      if (meta) {
        if (compareVersions(version.version, meta.minVersion) < 0) {
          meta.minVersion = version.version;
        }
        if (compareVersions(lineageMaxVersion, meta.maxVersion) > 0) {
          meta.maxVersion = lineageMaxVersion;
        }
      }
      prependVersionItems(currentLineageSection, matchingItems);
    } else {
      currentLineageSection = {
        key: lineageSectionKey,
        heading: '',
        lineageMeta: {
          lineage,
          minVersion: version.version,
          maxVersion: lineageMaxVersion,
        },
        items: [],
      };
      prependVersionItems(currentLineageSection, matchingItems);
      sections.push(currentLineageSection);
    }
  });

  sections.forEach((section) => {
    const regularItems = section.items.filter((item) => !item.dedupeKey);
    const dedupeItems = section.items.filter((item) => item.dedupeKey);
    section.items = [...regularItems, ...dedupeItems];
  });

  return sections.filter((section) => section.items.some((item) => !item.dedupeKey));
}

export function WhatsNewSheetContent({ onClose, payload }: IContextSheetContentProps) {
  const { t } = useTranslation();
  const isLargeScreen = useIsLargeScreen();
  const insets = useSafeAreaInsets();
  const scrollContentBottomPadding = isLargeScreen ? 16 : Math.max(insets.bottom, 12);
  const sheetPayload = payload as IWhatsNewSheetPayload | undefined;
  const isAutoTriggered = sheetPayload?.source === 'auto';
  const [autoShowDisabled, setAutoShowDisabled] = useState(false);

  const sections = useMemo<IWhatsNewSection[] | null>(() => {
    try {
      const context = {
        platform: Platform.OS as WhatsNewMatchPlatform,
        isLargeScreen,
      };
      const latestDisplayVersion = findLatestVersionWithSubstantiveContent(
        APP_VERSION,
        whatsNew.versions,
        context,
      );

      if (!latestDisplayVersion) {
        return [];
      }

      return buildSections(
        whatsNew.versions,
        context,
        latestDisplayVersion.version,
        sheetPayload?.source === 'auto' ? sheetPayload.lastSeenVersion : undefined,
      );
    } catch (error) {
      console.warn('[WhatsNewSheetContent] Failed to build what\'s-new sections', error);
      return null;
    }
  }, [isLargeScreen, sheetPayload?.lastSeenVersion, sheetPayload?.source]);

  useEffect(() => {
    if (isAutoTriggered) return;

    const checkPreference = async () => {
      try {
        const stored = await AsyncStorage.getItem(WHATS_NEW_AUTO_SHOW_DISABLED_STORAGE_KEY);
        setAutoShowDisabled(stored === 'true');
      } catch (error) {
        console.warn('[WhatsNewSheetContent] Failed to read what\'s-new preference', error);
      }
    };

    checkPreference();
  }, [isAutoTriggered]);

  const handleNeverShowAgain = useCallback(async () => {
    try {
      await AsyncStorage.setItem(WHATS_NEW_AUTO_SHOW_DISABLED_STORAGE_KEY, 'true');
      setAutoShowDisabled(true);
      onClose?.();
    } catch (error) {
      console.warn('[WhatsNewSheetContent] Failed to save what\'s-new preference', error);
    }
  }, [onClose]);

  const handleShowOnNewVersions = useCallback(async () => {
    try {
      await AsyncStorage.removeItem(WHATS_NEW_AUTO_SHOW_DISABLED_STORAGE_KEY);
      setAutoShowDisabled(false);
    } catch (error) {
      console.warn('[WhatsNewSheetContent] Failed to save what\'s-new preference', error);
    }
  }, []);

  if (sections === null) {
    return null;
  }

  return (
    <VStack className="min-h-0 flex-1">
      <SheetScrollView
        {...DRAWER_SCROLL_CLASSES}
        contentContainerStyle={{ paddingBottom: scrollContentBottomPadding }}
      >
        <VStack className="items-center pb-3">
          <HugeIconsWrapper icon={HotAirBalloonIcon} size={56} className="color-tertiary-500" />
        </VStack>
        <VStack className="gap-5">
          {sections.length === 0 ? (
            <Text className="text-typography-500">{t('whatsNew.empty')}</Text>
          ) : (
            sections.map((section) => (
              <VStack key={section.key} className="gap-2">
                {section.lineageMeta ? (
                  <HStack className="flex-wrap items-baseline gap-x-1.5 gap-y-0">
                    <Heading size="sm" className="text-typography-700">
                      {t('whatsNew.lineageLabel', { lineage: section.lineageMeta.lineage })}
                    </Heading>
                    <Text size="sm" className="font-normal text-typography-500">
                      {section.lineageMeta.minVersion === section.lineageMeta.maxVersion
                        ? `(${section.lineageMeta.minVersion})`
                        : `(${section.lineageMeta.minVersion} – ${section.lineageMeta.maxVersion})`}
                    </Text>
                  </HStack>
                ) : section.currentVersionMeta ? (
                  <HStack className="flex-wrap items-baseline gap-x-1.5 gap-y-0">
                    <Heading size="sm" className="text-typography-700">
                      {section.currentVersionMeta.mode === 'catchUp'
                        ? t('whatsNew.catchUpReleaseTitle')
                        : section.currentVersionMeta.mode === 'recent'
                          ? t('whatsNew.recentUpdatesTitle')
                          : t('whatsNew.currentReleaseTitle')}
                    </Heading>
                    <Text size="sm" className="font-normal text-typography-500">
                      {section.currentVersionMeta.mode === 'current' ||
                      section.currentVersionMeta.minVersion === section.currentVersionMeta.maxVersion
                        ? `(${section.currentVersionMeta.maxVersion})`
                        : `(${section.currentVersionMeta.minVersion} – ${section.currentVersionMeta.maxVersion})`}
                    </Text>
                  </HStack>
                ) : (
                  <Heading size="sm" className="text-typography-700">
                    {section.heading}
                  </Heading>
                )}
                <VStack className="gap-2">
                  {section.items.map((item, index) => (
                    <HStack key={`${section.key}-${index}`} className="items-start gap-2">
                      <Text className="text-typography-500 leading-6">{'\u2022'}</Text>
                      <Text className="flex-1 text-typography-800 leading-6">{item.text}</Text>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            ))
          )}
          <VStack className="items-center gap-3 my-4">
            <Button size="md" onPress={onClose}>
              <ButtonText>{t('common.continue', 'Continue')}</ButtonText>
            </Button>
            {isAutoTriggered ? (
              <Button
                variant="link"
                action="primary"
                size="sm"
                className="h-auto p-0"
                onPress={handleNeverShowAgain}>
                <ButtonText>{t('whatsNew.neverShowAgain')}</ButtonText>
              </Button>
            ) : autoShowDisabled ? (
              <Button
                variant="link"
                action="primary"
                size="sm"
                className="h-auto p-0"
                onPress={handleShowOnNewVersions}>
                <ButtonText>{t('whatsNew.showOnNewVersions')}</ButtonText>
              </Button>
            ) : null}
          </VStack>
        </VStack>
      </SheetScrollView>
    </VStack>
  );
}

WhatsNewSheetContent.sheetConfig = {
  largeScreenMode: 'drawer',
  size: 'md',
  height: 'lg',
  additionalSnapPoints: ['90%'],
  flushBottom: true,
  scrollableContent: true,
  integratedNativeSheetScrollGestures: true,
} satisfies ISheetConfig;
